import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronRight, CheckCircle, Lock, Database, Code, Layers, BookOpen } from 'lucide-react';
import { Button } from '../ui/button';
import {
  roadmapSteps,
  getCompletedStepIds,
  markStepCompleted,
  isStepUnlocked,
  type RoadmapStep,
} from '../../data/datadventure/roadmap';

const categoryIcons: Record<string, React.ElementType> = {
  sql: Database,
  python: Code,
  'data-engineering': Layers,
  linkedin: BookOpen,
};

export default function RoadmapView() {
  const navigate = useNavigate();
  const [completedIds, setCompletedIds] = useState<string[]>([]);

  useEffect(() => {
    setCompletedIds(getCompletedStepIds());
  }, []);

  const goToPractice = (step: RoadmapStep) => {
    navigate('/cursos', {
      state: {
        fromRoadmap: true,
        category: step.target.category,
        level: step.target.level,
        topicId: step.target.topicId,
        pythonTopicId: step.target.pythonTopicId,
      },
    });
  };

  const handleMarkCompleted = (stepId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    markStepCompleted(stepId);
    setCompletedIds(getCompletedStepIds());
  };

  return (
    <div className="space-y-4">
      <p className="text-slate-600 dark:text-slate-400 mb-6 text-center">
        Recorré el roadmap desde 0. Cada nodo te lleva a una práctica. Completá y desbloqueá el siguiente.
      </p>

      <div className="space-y-3 max-w-2xl mx-auto">
        {roadmapSteps.map((step, index) => {
          const completed = completedIds.includes(step.id);
          const unlocked = isStepUnlocked(step.id, completedIds);
          const Icon = categoryIcons[step.target.category] || BookOpen;

          return (
            <motion.div
              key={step.id}
              initial={{ opacity: 0, x: -12 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.05 }}
              className={`relative rounded-xl border-2 transition-all ${
                unlocked
                  ? 'bg-white/95 dark:bg-slate-800/95 border-slate-200 dark:border-slate-600 hover:border-emerald-400 dark:hover:border-emerald-500'
                  : 'bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700 opacity-80'
              }`}
            >
              <div className="flex items-center gap-4 p-4">
                {/* Orden / estado */}
                <div className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center bg-slate-100 dark:bg-slate-700">
                  {completed ? (
                    <CheckCircle className="w-6 h-6 text-emerald-500" />
                  ) : !unlocked ? (
                    <Lock className="w-5 h-5 text-slate-400" />
                  ) : (
                    <span className="text-sm font-bold text-slate-600 dark:text-slate-300">
                      {step.order}
                    </span>
                  )}
                </div>

                {/* Icono categoría */}
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-emerald-100 dark:bg-emerald-900/40 flex items-center justify-center text-emerald-600 dark:text-emerald-400">
                  <Icon className="w-5 h-5" />
                </div>

                {/* Título y descripción */}
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-slate-900 dark:text-white">
                    {step.title}
                  </h3>
                  <p className="text-sm text-slate-600 dark:text-slate-400 truncate">
                    {step.description}
                  </p>
                </div>

                {/* Acciones */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  {completed && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-slate-500"
                      onClick={(e) => handleMarkCompleted(step.id, e)}
                      title="Ya completado"
                    >
                      Completado
                    </Button>
                  )}
                  {unlocked && !completed && (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => handleMarkCompleted(step.id, e)}
                        title="Marcar como completado sin practicar"
                      >
                        Marcar completado
                      </Button>
                      <Button
                        size="sm"
                        className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1"
                        onClick={() => goToPractice(step)}
                      >
                        Ir a la práctica
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </>
                  )}
                </div>
              </div>

              {/* Línea conectora al siguiente (grafo) */}
              {index < roadmapSteps.length - 1 && (
                <div className="absolute left-[2.25rem] top-full w-0.5 h-3 bg-slate-200 dark:bg-slate-600" style={{ marginTop: 2 }} />
              )}
            </motion.div>
          );
        })}
      </div>

      <p className="text-sm text-slate-500 dark:text-slate-400 text-center mt-6">
        {completedIds.length} de {roadmapSteps.length} pasos completados
      </p>
    </div>
  );
}
